import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, LogIn, AlertCircle, Heart, Droplet, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';

const SignIn = () => {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      let redirectPath = '/profile';
      if (user?.role === 'admin') redirectPath = '/admin';
      else if (user?.role === 'center_admin') redirectPath = '/profile';
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    const result = await signIn(email, password);

    if (result.success) {
      toast({
        title: 'Welcome back!',
        description: 'You have been signed in successfully.',
      });
      await new Promise(resolve => setTimeout(resolve, 100));
      let redirectPath = '/profile';
      if (result.user?.role === 'admin') redirectPath = '/admin';
      else if (result.user?.role === 'center_admin') redirectPath = '/profile';
      navigate(redirectPath, { replace: true });
    } else {
      setError(result.error);
      toast({
        title: 'Sign In Failed',
        description: result.error,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';
  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Sign In', url: `${siteUrl}/signin` }
  ];

  return (
    <>
      <SEOHead
        title="Sign In to DonorsHouse | Access Your Donor Account"
        description="Sign in to your DonorsHouse account to access your donor profile, manage your donation preferences, and connect with recipients in need."
        keywords="sign in, login, donor account, DonorsHouse login, access account, donor profile"
        breadcrumbs={breadcrumbs}
      />

      <div className="min-h-screen flex">

        {/* ============================================================
            LEFT PANEL — brand hero (desktop only)
        ============================================================ */}
        <div
          data-testid="signin-hero-panel"
          className="hidden lg:flex lg:w-1/2 signin-hero-panel flex-col items-center justify-center p-12 text-white relative"
          aria-hidden="true"
        >
          {/* Background hero image */}
          <div className="absolute inset-0 overflow-hidden rounded-none">
            <img
              src="/signin-hero.jpg"
              alt=""
              className="w-full h-full object-cover opacity-20"
              loading="eager"
            />
          </div>

          {/* Content overlay */}
          <div className="relative z-10 text-center max-w-sm">
            {/* Animated heart */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                <Heart className="h-12 w-12 text-white animate-heartbeat" fill="currentColor" />
              </div>
            </div>

            <p className="text-3xl font-extrabold mb-4 leading-tight">
              Every Donation<br />Saves a Life
            </p>
            <p className="text-red-100 text-lg mb-10 leading-relaxed">
              Welcome back to the DonorsHouse community. Your generosity makes a difference every single day.
            </p>

            {/* Impact stats */}
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-extrabold text-white">195+</div>
                <div className="text-xs text-red-100 mt-0.5 font-medium">Countries</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-white">8</div>
                <div className="text-xs text-red-100 mt-0.5 font-medium">Lives per donor</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-white">24/7</div>
                <div className="text-xs text-red-100 mt-0.5 font-medium">Active</div>
              </div>
            </div>

            {/* Floating icons */}
            <div className="absolute top-16 left-8 opacity-40 animate-float">
              <Droplet className="h-8 w-8 text-white" />
            </div>
            <div className="absolute bottom-24 right-8 opacity-40 animate-float" style={{ animationDelay: '1s' }}>
              <Globe className="h-10 w-10 text-white" />
            </div>
          </div>
        </div>

        {/* ============================================================
            RIGHT PANEL — sign in form
        ============================================================ */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-gray-50 lg:bg-white">
          <div className="w-full max-w-md">

            {/* Mobile header (only visible on mobile) */}
            <div className="lg:hidden text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-2xl mb-4">
                <Heart className="h-8 w-8 text-red-700" fill="currentColor" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Welcome Back</h1>
              <p className="text-gray-700 font-medium">Sign in to your DonorsHouse account</p>
            </div>

            {/* Desktop header */}
            <div className="hidden lg:block mb-8">
              <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Welcome back</h1>
              <p className="text-gray-700 font-medium">Sign in to continue saving lives</p>
            </div>

            {/* Error message */}
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in"
              >
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-gray-800 font-semibold mb-1.5 block text-sm">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    className="pl-11 h-12 border-gray-200 focus:border-red-400 rounded-xl"
                    disabled={loading}
                    autoComplete="email"
                    aria-describedby={error ? 'signin-error' : undefined}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password" className="text-gray-800 font-semibold mb-1.5 block text-sm">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" aria-hidden="true" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="pl-11 h-12 border-gray-200 focus:border-red-400 rounded-xl"
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-red-700 hover:bg-red-800 text-white h-12 text-base font-bold rounded-xl btn-transition mt-2"
                disabled={loading}
                aria-label={loading ? 'Signing in, please wait' : 'Sign in to your account'}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
                    Signing in…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <LogIn className="h-5 w-5" aria-hidden="true" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>

            {/* Register link */}
            <p className="text-center text-sm text-gray-600 mt-6">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-red-700 hover:text-red-800 font-bold underline-offset-2 hover:underline"
              >
                Register as a donor
              </Link>
            </p>

            {/* Trust indicators */}
            <div className="mt-10 pt-6 border-t border-gray-100">
              <p className="text-center text-xs text-gray-700 mb-3 font-semibold">Trusted platform for</p>
              <div className="flex items-center justify-center gap-6 text-xs text-gray-800 font-medium">
                <span className="flex items-center gap-1">
                  <Droplet className="h-3.5 w-3.5 text-red-700" aria-hidden="true" />
                  Blood Donation
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="h-3.5 w-3.5 text-red-700" aria-hidden="true" />
                  Organ Donation
                </span>
                <span className="flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5 text-red-700" aria-hidden="true" />
                  195+ Countries
                </span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </>
  );
};

export default SignIn;
